import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, ClipboardList, X, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * ComposeAdvisorRequest — inline card that lives inside the CRM Client drawer
 * so any founder (or Chris himself) can send a document request to the client
 * in front of them without leaving the drawer.
 *
 * Uses the same `/admin/advisor/document-requests` endpoint the portal reads
 * from — no separate flow. Pre-fills the client's email from the drawer, so
 * the founder only types the title / checklist.
 */
export default function ComposeAdvisorRequest({ clientEmail, clientName }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [items, setItems] = useState([""]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle(""); setDescription(""); setDueAt(""); setItems([""]);
  };

  const send = async () => {
    if (!clientEmail) { toast.error("This client has no email on file yet."); return; }
    if (!title.trim()) { toast.error("Give the request a short title."); return; }
    setSubmitting(true);
    try {
      const cleanItems = items.map((i) => i.trim()).filter(Boolean);
      await api.post("/admin/advisor/document-requests", {
        client_email: clientEmail,
        title: title.trim(),
        description: description.trim() || null,
        items: cleanItems,
        due_at: dueAt || null,
      });
      toast.success(`Sent to ${clientName || clientEmail}. They'll see it in their portal.`);
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="compose-advisor-request" className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/6 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[#a3823f]" />
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Chris's document request</p>
        </div>
        {!open ? (
          <button
            type="button"
            data-testid="compose-advisor-open"
            onClick={() => setOpen(true)}
            disabled={!clientEmail}
            title={!clientEmail ? "Add a primary contact email first" : "Send Chris's request to this client"}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-40"
          >
            <Send className="h-3 w-3" />
            Send request
          </button>
        ) : (
          <button
            type="button"
            data-testid="compose-advisor-cancel"
            onClick={() => { reset(); setOpen(false); }}
            className="text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors duration-300 p-1"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {!open ? (
        <p className="text-[11px] text-[#1e3d3a]/55 mt-2 leading-relaxed">
          Ask {clientName || "this client"} for any records Chris needs — arrives in their portal + inbox.
        </p>
      ) : (
        <div className="mt-3 space-y-2.5">
          <input
            data-testid="compose-advisor-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title, e.g. “Q3 close-out documents”"
            className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
          />
          <textarea
            data-testid="compose-advisor-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What Chris needs and why (optional)"
            className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300 resize-none"
          />
          <div className="space-y-1.5">
            <p className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/50">Checklist</p>
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <input
                  data-testid={`compose-advisor-item-${idx}`}
                  value={it}
                  onChange={(e) => setItems((prev) => prev.map((x, i) => i === idx ? e.target.value : x))}
                  placeholder={`Item ${idx + 1} — e.g. “Q3 P&L”`}
                  className="flex-1 rounded-lg border border-[#1e3d3a]/12 bg-white px-3 py-1.5 text-[12px] text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
                />
                <button
                  type="button"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                  disabled={items.length === 1}
                  className="rounded-full p-1 text-[#1e3d3a]/30 hover:text-[#b23a48] disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-300"
                  aria-label="Remove item"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="compose-advisor-add-item"
              onClick={() => setItems((prev) => [...prev, ""])}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              <Plus className="h-3 w-3" /> Add item
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 flex-1">
              Due date <span className="text-[#1e3d3a]/30">(optional)</span>
              <input
                type="date"
                data-testid="compose-advisor-due"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[12px] text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
              />
            </label>
          </div>
          <button
            type="button"
            data-testid="compose-advisor-send"
            onClick={send}
            disabled={submitting || !title.trim()}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-40 transition-colors duration-300"
          >
            {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</>
              : <><Send className="h-3.5 w-3.5" /> Send to {clientName || clientEmail || "client"}</>}
          </button>
        </div>
      )}
    </div>
  );
}
